/**
 * MediaPipe Landmark to Pixel Coordinate Mapping
 * Handles conversion from normalized landmarks to video content pixels
 */

import type { ContentRect } from '../video/getContentRect';

export interface NormalizedLandmark {
  x: number;
  y: number;
  z?: number;
  visibility?: number;
}

export interface PixelLandmark {
  x: number;
  y: number;
  z: number;
  visibility: number;
}

/**
 * Convert normalized landmarks (0-1 space) to pixel coordinates within content rect
 * This ensures landmarks map to actual video content, not letterbox/pillarbox areas
 */
export function normToPixels(
  landmarks: NormalizedLandmark[],
  contentRect: ContentRect
): PixelLandmark[] {
  return landmarks.map(lm => ({
    x: contentRect.x + lm.x * contentRect.w,
    y: contentRect.y + lm.y * contentRect.h,
    z: lm.z ?? 0,
    visibility: lm.visibility ?? 1
  }));
}

/**
 * Convert MediaPipe landmarks array format to normalized landmark objects
 */
export function mediapiperToNormalized(
  landmarks: Array<[number, number, number, number?]>
): NormalizedLandmark[] {
  return landmarks.map(([x, y, z, visibility]) => ({
    x,
    y,
    z: z ?? 0,
    visibility: visibility ?? 1
  }));
}

/**
 * Apply mirroring transformation if needed
 * Call this ONCE either here or in CSS - never both
 */
export function applyMirroring(
  landmarks: PixelLandmark[],
  contentRect: ContentRect,
  shouldMirror: boolean = false
): PixelLandmark[] {
  if (!shouldMirror) return landmarks;
  
  // Flip x coordinates around content center
  const centerX = contentRect.x + contentRect.w / 2;
  
  return landmarks.map(lm => ({
    ...lm,
    x: centerX + (centerX - lm.x) // Mirror around center
  }));
}

/**
 * Check if landmark is within content bounds (useful for filtering outliers)
 */
export function isWithinBounds(
  landmark: PixelLandmark,
  contentRect: ContentRect,
  margin: number = 0
): boolean {
  return (
    landmark.x >= contentRect.x - margin &&
    landmark.x <= contentRect.x + contentRect.w + margin &&
    landmark.y >= contentRect.y - margin &&
    landmark.y <= contentRect.y + contentRect.h + margin
  );
}
